import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { MessageSquare, CheckCircle2, XCircle, Clock, Ban } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * SMS Activity — recent Twilio sends for one shop, with the price
 * Twilio charged us cached on `sms_log.price_amount`. Master-admin
 * only (endpoint is guarded).
 *
 * The backend does a lazy price-refresh on GET so brand-new sends
 * appear here with cost data within a page refresh or two.
 */
const STATUS_ICON = {
  delivered:     { Icon: CheckCircle2, tone: '#059669', label: 'Delivered' },
  sent:          { Icon: Clock,        tone: '#0284c7', label: 'Sent (pending delivery)' },
  queued:        { Icon: Clock,        tone: '#0284c7', label: 'Queued' },
  sending:       { Icon: Clock,        tone: '#0284c7', label: 'Sending' },
  failed:        { Icon: XCircle,      tone: '#dc2626', label: 'Failed' },
  undelivered:   { Icon: XCircle,      tone: '#dc2626', label: 'Undelivered' },
  disabled_skipped:  { Icon: Ban, tone: '#6b7280', label: 'Skipped (SMS disabled)' },
  optout_skipped:    { Icon: Ban, tone: '#6b7280', label: 'Skipped (customer opt-out)' },
  skipped_non_uk:    { Icon: Ban, tone: '#6b7280', label: 'Skipped (non-UK number)' },
  skipped_no_config: { Icon: Ban, tone: '#6b7280', label: 'Skipped (not configured)' },
  simulated:     { Icon: MessageSquare, tone: '#9333ea', label: 'Simulated' },
};

const fmtMoney = (amount, unit) => {
  if (amount == null) return '—';
  const symbol = { USD: '$', GBP: '£', EUR: '€' }[unit] || (unit ? unit + ' ' : '');
  return `${symbol}${Number(amount).toFixed(4)}`;
};

const fmtWhen = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

export const SmsActivityPanel = ({ shopId }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [includeSkipped, setIncludeSkipped] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setError('');
        setData(null);
        const { data: { session } } = await supabase.auth.getSession();
        const res = await fetch(
          `${BACKEND_URL}/api/admin/shops/${shopId}/sms-activity?limit=25&days=90&include_skipped=${includeSkipped ? 'true' : 'false'}`,
          { headers: { Authorization: `Bearer ${session?.access_token}` } },
        );
        const j = await res.json();
        if (cancelled) return;
        if (!res.ok) throw new Error(j.detail || `HTTP ${res.status}`);
        setData(j);
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [shopId, includeSkipped]);

  return (
    <div className="panel" data-testid="shop-sms-activity">
      <div className="ph" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <MessageSquare size={14} /> SMS activity
        </h3>
        {data && (
          <span style={{ marginLeft: 'auto', fontSize: 'var(--fs-sm)', color: 'var(--text-mute)' }}>
            {data.total} sends
            {data.cost_total > 0 && (
              <> · total <b style={{ color: 'var(--text)' }}>{fmtMoney(data.cost_total, data.cost_unit)}</b></>
            )}
          </span>
        )}
        <label
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            fontSize: 'var(--fs-sm)', color: 'var(--text-mute)',
            marginLeft: data ? 12 : 'auto',
            cursor: 'pointer',
          }}
          data-testid="sms-activity-include-skipped-label"
        >
          <input
            type="checkbox"
            checked={includeSkipped}
            onChange={(e) => setIncludeSkipped(e.target.checked)}
            data-testid="sms-activity-include-skipped"
          />
          Show blocked attempts
        </label>
      </div>

      {error && (
        <div style={{ padding: 16, color: 'var(--red)', fontSize: 'var(--fs-sm)' }} data-testid="sms-activity-error">
          Failed to load: {error}
        </div>
      )}

      {!error && !data && (
        <div style={{ padding: 20, color: 'var(--text-mute)', fontSize: 'var(--fs-sm)' }}>Loading…</div>
      )}

      {data && data.rows.length === 0 && (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-mute)', fontSize: 'var(--fs-base)' }}>
          {includeSkipped
            ? 'No SMS activity in the last 90 days.'
            : 'No successful SMS sends in the last 90 days. Tick "Show blocked attempts" to see guard-skipped rows.'}
        </div>
      )}

      {data && data.rows.length > 0 && (
        <div style={{ maxHeight: 380, overflowY: 'auto' }}>
          {data.rows.map((r) => {
            const meta = STATUS_ICON[r.status] || { Icon: MessageSquare, tone: '#6b7280', label: r.status || 'unknown' };
            const Icon = meta.Icon;
            return (
              <div
                key={r.id}
                data-testid={`sms-row-${r.id}`}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '20px 1fr auto',
                  gap: 10,
                  padding: '10px 16px',
                  borderTop: '1px solid var(--line2)',
                  alignItems: 'flex-start',
                  fontSize: 'var(--fs-sm)',
                }}
              >
                <Icon size={14} style={{ color: meta.tone, marginTop: 3 }} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'baseline', flexWrap: 'wrap' }}>
                    <b style={{ fontSize: 'var(--fs-sm)' }}>
                      {(r.template_key || '').replace(/_/g, ' ') || 'ad-hoc'}
                    </b>
                    <span style={{ color: 'var(--text-mute)', fontSize: 'var(--fs-xs)' }}>
                      to {r.to_phone || '—'}
                    </span>
                    <span style={{
                      color: meta.tone, fontSize: 'var(--fs-xs)',
                      background: meta.tone + '15', padding: '1px 6px', borderRadius: 4,
                    }}>{meta.label}</span>
                  </div>
                  <div style={{
                    color: 'var(--text-mute)', fontSize: 'var(--fs-xs)',
                    marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap', maxWidth: '100%',
                  }} title={r.body || ''}>
                    {r.body || ''}
                  </div>
                  {r.error_message && (
                    <div style={{ color: '#dc2626', fontSize: 'var(--fs-xs)', marginTop: 2 }}>
                      {r.error_code ? `[${r.error_code}] ` : ''}{r.error_message}
                    </div>
                  )}
                </div>
                <div style={{ textAlign: 'right', fontSize: 'var(--fs-xs)', color: 'var(--text-mute)' }}>
                  <div>{fmtWhen(r.sent_at)}</div>
                  <div style={{ fontFamily: 'var(--font-mono, monospace)', color: 'var(--text)', marginTop: 2 }}>
                    {fmtMoney(r.price_amount, r.price_unit)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
